/**
 * 应用中心模块配置
 * 统一管理应用中心模块的路由、菜单、权限配置
 * @author 开发团队
 * @date 2024-01-01
 */

import type { ModuleConfig } from '@/types/module-config'

/**
 * 应用中心模块配置
 */
const moduleConfig: ModuleConfig = {
  id: 'uapm',
  name: '应用中心',
  description: '应用中心模块，包含应用发布和公告发布功能',
  version: '1.0.0',
  enabled: true,

  // 路由配置
  routes: [
    {
      path: '/uapm',
      name: 'UAPM',
      component: '@/layouts/index.vue',
      redirect: '/uapm/publish',
      meta: {
        title: '应用中心',
        icon: 'Platform',
        roles: ['admin', 'super_admin', 'operator'],
        alwaysShow: true,
        order: 200
      },
      children: [
        {
          path: 'publish',
          name: 'UAPMPublish',
          component: '@/modules/uapm/pages/publish.vue',
          meta: {
            title: '应用发布',
            icon: 'Upload',
            roles: ['admin', 'super_admin', 'operator'],
            permissions: ['uapm:publish:view'],
            keepAlive: true
          }
        },
        {
          path: 'notice',
          name: 'UAPMNotice',
          component: '@/modules/uapm/pages/notice.vue',
          meta: {
            title: '公告发布',
            icon: 'Bell',
            roles: ['admin', 'super_admin', 'operator'],
            permissions: ['uapm:notice:view'],
            keepAlive: true
          }
        },
        {
          path: 'a2ui-test',
          name: 'UAPMA2uiTest',
          component: '@/modules/uapm/pages/a2ui-test.vue',
          meta: {
            title: 'A2UI 渲染测试',
            icon: 'Test',
            roles: ['admin', 'super_admin'],
            hidden: true
          }
        }
      ]
    }
  ],

  // 权限配置
  permissions: [
    // 应用发布权限
    {
      code: 'uapm:publish:view',
      name: '查看应用发布',
      type: 'menu',
      description: '查看应用发布页面',
      apiPath: '/uapm/publish'
    },
    {
      code: 'uapm:publish:create',
      name: '创建应用',
      type: 'button',
      description: '创建新应用',
      apiPath: '/uapm/applications'
    },
    {
      code: 'uapm:publish:edit',
      name: '编辑应用',
      type: 'button',
      description: '编辑应用信息',
      apiPath: '/uapm/applications/:id'
    },
    {
      code: 'uapm:publish:delete',
      name: '删除应用',
      type: 'button',
      description: '删除应用',
      apiPath: '/uapm/applications/:id'
    },
    {
      code: 'uapm:publish:detail',
      name: '查看应用详情',
      type: 'operation',
      description: '查看应用详细信息',
      apiPath: '/uapm/applications/:id'
    },
    {
      code: 'uapm:publish:status',
      name: '修改应用状态',
      type: 'operation',
      description: '发布/下架应用',
      apiPath: '/uapm/applications/:id/status'
    },
    {
      code: 'uapm:publish:version',
      name: '版本管理',
      type: 'operation',
      description: '管理应用版本',
      apiPath: '/uapm/applications/:id/versions'
    },

    // 公告发布权限
    {
      code: 'uapm:notice:view',
      name: '查看公告发布',
      type: 'menu',
      description: '查看公告发布页面',
      apiPath: '/uapm/notice'
    },
    {
      code: 'uapm:notice:create',
      name: '创建公告',
      type: 'button',
      description: '创建新公告',
      apiPath: '/uapm/notices'
    },
    {
      code: 'uapm:notice:edit',
      name: '编辑公告',
      type: 'button',
      description: '编辑公告内容',
      apiPath: '/uapm/notices/:id'
    },
    {
      code: 'uapm:notice:delete',
      name: '删除公告',
      type: 'button',
      description: '删除公告',
      apiPath: '/uapm/notices/:id'
    },
    {
      code: 'uapm:notice:detail',
      name: '查看公告详情',
      type: 'operation',
      description: '查看公告详细信息',
      apiPath: '/uapm/notices/:id'
    },
    {
      code: 'uapm:notice:publish',
      name: '发布公告',
      type: 'operation',
      description: '发布/撤回公告',
      apiPath: '/uapm/notices/:id/publish'
    },
    {
      code: 'uapm:notice:target',
      name: '设置目标用户',
      type: 'operation',
      description: '设置公告目标用户',
      apiPath: '/uapm/notices/:id/target'
    }
  ],

  // 导航配置
  navigation: {
    name: 'uapm',
    title: '应用中心',
    icon: 'Platform',
    permissions: ['uapm:view'],
    order: 200,
    items: [
      {
        id: 'uapm-publish',
        title: '应用发布',
        path: '/uapm/publish',
        icon: 'Upload',
        order: 1,
        permissions: ['uapm:publish:view'],
        module: 'uapm',
        component: '@/modules/uapm/pages/publish.vue'
      },
      {
        id: 'uapm-notice',
        title: '公告发布',
        path: '/uapm/notice',
        icon: 'Bell',
        order: 2,
        permissions: ['uapm:notice:view'],
        module: 'uapm',
        component: '@/modules/uapm/pages/notice.vue'
      }
    ]
  }
}

export default moduleConfig
